import {
  BYTES_POR_AMOSTRA,
  TAXA_HZ,
} from '@modules/aawz-intelligence-v2/utils/avatar-ao-vivo/protocolo'

// ★ PCM cru → WAV tocável no `<audio>` (spec-009) — função PURA.
//
// ## Por que isto precisa existir
//
// **O LiveAvatar LITE publica SÓ VÍDEO na sala.** Medido em 18/09/2026, com o
// SDK instrumentado: a faixa de vídeo chega (`video:live:enabled=true`), a de
// áudio NUNCA — `trackSubscribed` de áudio: zero. E faz sentido no modo LITE:
// quem produz o áudio somos nós, e ele só renderiza os lábios a partir dele;
// devolvê-lo seria redundante.
//
// A consequência é que **o som tem de sair daqui**. O owner viu isso em 30
// segundos de uso: *"video está sem som"*, com o rosto renderizando e a
// legenda andando.
//
// ## Por que WAV, e não tocar o PCM direto
//
// `<audio>` não toca PCM cru: ele precisa de um contêiner que diga a taxa, o
// número de canais e a profundidade. O WAV é o mais barato que existe — 44
// bytes de cabeçalho na frente dos mesmos bytes, sem recodificar nada. O áudio
// que sai do alto-falante é **byte a byte o que foi mandado ao provedor**, que
// é o que mantém a sincronia possível.
//
// ⚠️ Nenhuma reamostragem, nenhuma conversão de formato: se este arquivo
// mexesse nas amostras, a fala local e os lábios do avatar contariam histórias
// diferentes.

/** Os 44 bytes do cabeçalho canônico (RIFF/WAVE, `fmt ` de 16 bytes, `data`). */
const TAMANHO_DO_CABECALHO = 44
const FORMATO_PCM = 1
const CANAIS = 1
const BITS_POR_AMOSTRA = BYTES_POR_AMOSTRA * 8

/** Escreve um inteiro little-endian como string binária de `bytes` chars. */
const _leInt = (valor, bytes) => {
  let saida = ''
  let resto = valor
  for (let i = 0; i < bytes; i += 1) {
    saida += String.fromCharCode(resto & 0xff)
    resto >>>= 8
  }
  return saida
}

/**
 * Embrulha um PCM 16-bit mono base64 num WAV base64.
 *
 * @param {string} pcmBase64 o áudio como o servidor mandou.
 * @param {number} [taxaHz] só para teste; em produção é a do protocolo.
 * @returns {string} o WAV em base64, ou `''` quando não há o que tocar
 *   (entrada vazia, base64 inválido, ambiente sem `atob`/`btoa`) — nunca
 *   lança, porque ficar sem som local é um estado previsto e o rosto continua.
 */
export const pcmParaWavBase64 = (pcmBase64, taxaHz = TAXA_HZ) => {
  if (!pcmBase64 || typeof pcmBase64 !== 'string') return ''
  if (typeof atob !== 'function' || typeof btoa !== 'function') return ''

  let dados
  try {
    dados = atob(pcmBase64)
  } catch (e) {
    return ''
  }
  // PCM de 16 bits tem comprimento PAR; um byte solto no fim viraria estalo.
  const tamanho = dados.length - (dados.length % BYTES_POR_AMOSTRA)
  if (tamanho <= 0) return ''

  const bytesPorSegundo = taxaHz * CANAIS * BYTES_POR_AMOSTRA
  const alinhamento = CANAIS * BYTES_POR_AMOSTRA

  const cabecalho =
    'RIFF' +
    _leInt(TAMANHO_DO_CABECALHO - 8 + tamanho, 4) +
    'WAVE' +
    'fmt ' +
    _leInt(16, 4) +
    _leInt(FORMATO_PCM, 2) +
    _leInt(CANAIS, 2) +
    _leInt(taxaHz, 4) +
    _leInt(bytesPorSegundo, 4) +
    _leInt(alinhamento, 2) +
    _leInt(BITS_POR_AMOSTRA, 2) +
    'data' +
    _leInt(tamanho, 4)

  try {
    return btoa(cabecalho + dados.slice(0, tamanho))
  } catch (e) {
    return ''
  }
}

/** A URI pronta para o `src` de um `<audio>`. Vazia quando não há áudio. */
export const pcmComoFonteDeAudio = (pcmBase64) => {
  const wav = pcmParaWavBase64(pcmBase64)
  return wav ? `data:audio/wav;base64,${wav}` : ''
}
